/**
 * referenceCheck.ts — what an admin typed into the agreements search box, decided before any
 * query is sent.
 *
 * THERE IS EXACTLY ONE REFERENCE SHAPE AND THERE HAS ONLY EVER BEEN ONE: `100.247.6` — a
 * six-digit serial grouped three-and-three, followed by a Damm check digit computed over those
 * six digits. The scheme shipped while `agreement_documents` was still EMPTY in production, so no
 * document any admin can hold was issued under the earlier `PL-XX-YYYY-NNNNNN` sketch. There is
 * no back catalogue, so there is no second language to speak, and this module deliberately does
 * not speak one. The dozen `PL-…` rows on staging are write-once test residue that cannot be
 * renumbered by design; they are simply not findable by reference search, which is the accepted
 * price of one shape rather than two forever.
 *
 * A `PL-…` STRING IS THEREFORE FREE TEXT. It is not recognised, not canonicalised and not refused
 * — it takes the same path as a half-remembered name, which is the only honest reading of a shape
 * that names nothing.
 *
 * THE CHECK DIGIT IS ONLY WORTH PRINTING IF SOMETHING CHECKS IT. Damm's quasigroup detects every
 * single-digit substitution and every transposition of adjacent digits — precisely the two ways
 * a number read off paper or repeated down a phone line goes wrong — so a reference that fails
 * it names a document that CANNOT EXIST. Sending that to the server returns an empty list, which
 * an admin reads as "we have no such agreement" when the truth is "you misread the number". That
 * gap between the two readings is the whole reason this module refuses instead of querying.
 *
 * NO SUPABASE CLIENT IN THIS MODULE'S GRAPH — the same bargain `documents.ts` makes, and for the
 * same reason: it is what lets every rule here be pinned by a unit test rather than by clicking.
 */

// ─── The check digit ─────────────────────────────────────────────────────────

/**
 * Damm's order-10 weakly totally anti-symmetric quasigroup — the standard table, and the one the
 * database computes with. ROWS ARE THE RUNNING INTERIM DIGIT, COLUMNS ARE THE NEXT INPUT DIGIT.
 *
 * ITS DIAGONAL IS ALL ZEROS, which is the property that lets the check digit simply be appended:
 * running the six serial digits followed by their own check digit through the table lands back on
 * 0, so validation and computation are the same walk.
 */
const DAMM_MATRIX: readonly (readonly number[])[] = [
    [0, 3, 1, 7, 5, 9, 8, 6, 4, 2],
    [7, 0, 9, 2, 1, 5, 4, 8, 6, 3],
    [4, 2, 0, 6, 8, 7, 1, 3, 5, 9],
    [1, 7, 5, 0, 9, 8, 3, 4, 2, 6],
    [6, 1, 2, 3, 0, 4, 5, 9, 7, 8],
    [3, 6, 7, 4, 2, 0, 9, 5, 8, 1],
    [5, 8, 6, 9, 7, 2, 0, 1, 3, 4],
    [8, 9, 4, 5, 3, 6, 2, 0, 1, 7],
    [9, 4, 3, 8, 6, 1, 7, 2, 0, 5],
    [2, 5, 8, 1, 4, 3, 6, 7, 9, 0],
];

const DIGITS_ONLY = /^\d+$/;

/**
 * The Damm check digit over a digit string.
 *
 * THROWS RATHER THAN COERCING. A caller that hands this non-digits has already failed to
 * recognise its own input, and a silent 0 for `"10O247"` would mint a check digit for a serial
 * nobody has — every caller in this module tests the shape first.
 */
export function dammCheckDigit(digits: string): number {
    if (!DIGITS_ONLY.test(digits)) {
        throw new TypeError(`dammCheckDigit expects digits only, received "${digits}"`);
    }
    let interim = 0;
    for (const digit of digits) {
        interim = DAMM_MATRIX[interim][Number(digit)];
    }
    return interim;
}

/** The six-digit serial a new-generation reference is built from. */
const SERIAL = /^\d{6}$/;

/** `100247` → `100.247.6`: the stored shape, grouping and check digit included. */
export function referenceForSerial(serial: string): string {
    if (!SERIAL.test(serial)) {
        throw new TypeError(`referenceForSerial expects six digits, received "${serial}"`);
    }
    return `${serial.slice(0, 3)}.${serial.slice(3)}.${dammCheckDigit(serial)}`;
}

// ─── What was typed ──────────────────────────────────────────────────────────

/**
 * The reading of a search box's contents.
 *
 * `failed-check-digit` IS NOT AN EMPTY RESULT AND MUST NOT BE TREATED AS ONE. It is the one
 * reading that suppresses the query entirely: there is nothing to look for, and the box says so.
 */
export type AgreementSearch =
    | { kind: 'empty' }
    /** A reference in its stored form: the dotted `100.247.6`, whatever spacing was typed. */
    | { kind: 'reference'; referenceNo: string }
    /** A reference's shape with the wrong check digit; `typed` is grouped for the message. */
    | { kind: 'failed-check-digit'; typed: string }
    | { kind: 'contract'; contractId: number }
    | { kind: 'text'; text: string };

/** `100.247.6` as printed. */
const DOTTED = /^\d{3}\.\d{3}\.\d$/;
/** The same seven digits typed straight through, which is what a keypad produces. */
const BARE = /^\d{7}$/;
/** A contract number, with or without the `#` the table prints it with. */
const CONTRACT_NUMBER = /^#?(\d{1,6})$/;

/**
 * The characters that would corrupt a PostgREST `or=(…)` expression rather than search for
 * themselves. Names do not contain them; a stray comma pasted in with a name used to turn the
 * whole request into a 400, so they are dropped here once instead of escaped at each call site.
 */
const FILTER_BREAKING = /[,()*%"\\]+/g;

export function parseAgreementSearch(input: string): AgreementSearch {
    const trimmed = input.trim();
    if (!trimmed) return { kind: 'empty' };

    /*
     * WHITESPACE INSIDE A REFERENCE IS NOISE, NOT SEPARATION. `100 247 6` read off a phone call is
     * the same reference as the printed `100.247.6`. There is no case to fold: a reference is
     * digits and dots and nothing else.
     */
    const compact = trimmed.replace(/\s+/g, '');

    if (DOTTED.test(compact) || BARE.test(compact)) {
        const digits = compact.replace(/\./g, '');
        const serial = digits.slice(0, 6);
        const typed = `${serial.slice(0, 3)}.${serial.slice(3)}.${digits[6]}`;
        if (dammCheckDigit(serial) !== Number(digits[6])) {
            return { kind: 'failed-check-digit', typed };
        }
        return { kind: 'reference', referenceNo: typed };
    }

    /*
     * A BARE NUMBER THAT IS NOT SEVEN DIGITS LONG IS A CONTRACT, NOT A SERIAL. Contract numbers
     * are the other identifier on this page and the only other thing an admin types digits for;
     * seven digits are claimed above because that is exactly what a reference is.
     */
    const contract = CONTRACT_NUMBER.exec(compact);
    if (contract) return { kind: 'contract', contractId: Number(contract[1]) };

    const text = trimmed.replace(FILTER_BREAKING, ' ').replace(/\s+/g, ' ').trim();
    return text ? { kind: 'text', text } : { kind: 'empty' };
}

// ─── Matching a fetched row ──────────────────────────────────────────────────

/** The three fields a client-side list needs to answer a search itself. */
export interface SearchableDocument {
    reference_no: string;
    contract_id: number | null;
    doc_type: string;
}

/** `HIRE_PURCHASE` → `hire purchase`, so the badge on screen is what gets typed. */
function typeHaystack(docType: string): string {
    return docType.replace(/[^a-z0-9]+/gi, ' ').trim().toLowerCase();
}

/**
 * Whether an already-fetched document answers the search — the lists that hold every row they
 * will ever show filter with this instead of going back to the server.
 *
 * A REFERENCE MATCHES EXACTLY, NEVER PARTIALLY. It is an identifier: `100.247.6` is one document
 * and the admin holding that number wants that document, not the eleven others whose serials
 * start the same way. Free text is the fuzzy path, and it deliberately does look inside the
 * reference — a half-remembered `100.247` is text, and finding it is better than nothing.
 */
export function matchesAgreementSearch(
    document: SearchableDocument,
    search: AgreementSearch,
): boolean {
    switch (search.kind) {
        case 'empty':
            return true;
        // Nothing can match a reference that cannot exist.
        case 'failed-check-digit':
            return false;
        case 'reference':
            return document.reference_no.trim() === search.referenceNo;
        case 'contract':
            return document.contract_id === search.contractId;
        case 'text': {
            const needle = search.text.toLowerCase();
            return (
                typeHaystack(document.doc_type).includes(needle) ||
                document.reference_no.toLowerCase().includes(needle)
            );
        }
    }
}
